/**
 * Form Error Boundary
 * 
 * WHY: The Lead Developer (DoD) mandated granular error boundaries
 * around MetadataForm to prevent one bad schema from crashing the
 * entire dashboard. This is critical for SCIF deployments where
 * debugging is limited.
 * 
 * @see .docs/architecture/software_iq_architecture_review.md - Expert 1
 */

import { Component, ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
  /** Schema ID for error reporting */
  schemaId?: string
  /** Callback when user requests retry */
  onRetry?: () => void
  /** Fallback UI to render on error */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary specifically for form components.
 * Catches rendering errors and displays a recovery UI.
 */
export class FormErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error for debugging (would go to telemetry in production)
    console.error('[FormErrorBoundary] Schema rendering failed:', {
      schemaId: this.props.schemaId,
      error: error.message,
      stack: errorInfo.componentStack,
    })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
    this.props.onRetry?.()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Form Loading Error
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Failed to render form
              {this.props.schemaId && (
                <span className="font-mono text-xs"> ({this.props.schemaId})</span>
              )}
              . This may indicate a schema configuration issue.
            </p>
            {this.state.error && (
              <pre className="rounded-md bg-muted p-3 text-xs overflow-auto max-h-32">
                {this.state.error.message}
              </pre>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleRetry}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      )
    }

    return this.props.children
  }
}
